import type { App } from "@upsquad/server";
import type { GroupJoinRequestDecision, GroupJoinRequestStatus } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetGroupsResponse = App["~Routes"]["group"][":communityId"]["get"]["response"]["200"];
type GetMyGroupsResponse = App["~Routes"]["group"][":communityId"]["me"]["get"]["response"]["200"];
type GetBrowseGroupsResponse = App["~Routes"]["group"][":communityId"]["browse"]["get"]["response"]["200"];
type GetMyGroupRequestsResponse = App["~Routes"]["group"][":communityId"]["requests"]["me"]["get"]["response"]["200"];
type GetGroupJoinRequestsResponse = App["~Routes"]["group"][":communityId"]["requests"]["get"]["response"]["200"];
type GetGroupJoinRequestsCountResponse = App["~Routes"]["group"][":communityId"]["requests"]["count"]["get"]["response"]["200"];
type JoinGroupRoute = App["~Routes"]["group"][":communityId"][":groupId"]["join"]["post"];
export type JoinGroupResponse = JoinGroupRoute["response"]["200"] | JoinGroupRoute["response"]["201"];

interface CreateGroupParams {
	communityId: string;
	name: string;
	description?: string;
}

interface DeleteGroupParams {
	communityId: string;
	groupId: string;
}

interface UpdateGroupParams {
	communityId: string;
	groupId: string;
	name?: string;
	description?: string;
	allow_self_join?: boolean;
}

interface AddMembersToGroupParams {
	communityId: string;
	groupId: string;
	userIds: string[];
}

interface RemoveMemberFromGroupParams {
	communityId: string;
	groupId: string;
	userId: string;
}

interface GroupMembershipParams {
	communityId: string;
	groupId: string;
}

interface GetGroupJoinRequestsParams {
	communityId: string;
	status?: GroupJoinRequestStatus;
}

interface DecideGroupJoinRequestParams {
	communityId: string;
	requestId: string;
	decision: GroupJoinRequestDecision;
}

export const groupApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getGroups: builder.query<GetGroupsResponse, string>({
			query:
				(communityId): EdenCall<GetGroupsResponse> =>
				(client) =>
					client.group({ communityId }).get(),
			providesTags: ["Groups"],
		}),
		getMyGroups: builder.query<GetMyGroupsResponse, string>({
			query:
				(communityId): EdenCall<GetMyGroupsResponse> =>
				(client) =>
					client.group({ communityId }).me.get(),
			providesTags: ["Groups", "MyGroups"],
		}),
		getBrowseGroups: builder.query<GetBrowseGroupsResponse, string>({
			query:
				(communityId): EdenCall<GetBrowseGroupsResponse> =>
				(client) =>
					client.group({ communityId }).browse.get(),
			providesTags: ["Groups", "BrowseGroups"],
		}),
		getMyGroupRequests: builder.query<GetMyGroupRequestsResponse, string>({
			query:
				(communityId): EdenCall<GetMyGroupRequestsResponse> =>
				(client) =>
					client.group({ communityId }).requests.me.get(),
			providesTags: ["MyGroupRequests"],
		}),
		getGroupJoinRequests: builder.query<GetGroupJoinRequestsResponse, GetGroupJoinRequestsParams>({
			query:
				({ communityId, status }): EdenCall<GetGroupJoinRequestsResponse> =>
				(client) =>
					client.group({ communityId }).requests.get({ query: { status } }),
			providesTags: ["GroupJoinRequests"],
		}),
		getGroupJoinRequestsCount: builder.query<GetGroupJoinRequestsCountResponse, string>({
			query:
				(communityId): EdenCall<GetGroupJoinRequestsCountResponse> =>
				(client) =>
					client.group({ communityId }).requests.count.get(),
			providesTags: ["GroupJoinRequestsCount"],
		}),
		createGroup: builder.mutation<unknown, CreateGroupParams>({
			query:
				({ communityId, name, description }) =>
				(client) =>
					client.group({ communityId }).post({ name, description }),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups"],
		}),
		deleteGroup: builder.mutation<unknown, DeleteGroupParams>({
			query:
				({ communityId, groupId }) =>
				(client) =>
					client.group({ communityId })({ groupId }).delete(),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups", "Members"],
		}),
		updateGroup: builder.mutation<unknown, UpdateGroupParams>({
			query:
				({ communityId, groupId, name, description, allow_self_join }) =>
				(client) =>
					client.group({ communityId })({ groupId }).patch({ name, description, allow_self_join }),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups", "Members"],
		}),
		addMembersToGroup: builder.mutation<unknown, AddMembersToGroupParams>({
			query:
				({ communityId, groupId, userIds }) =>
				(client) =>
					client.group({ communityId })({ groupId }).members.post({ user_ids: userIds }),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups", "Members", "GroupJoinRequests", "GroupJoinRequestsCount"],
		}),
		removeMemberFromGroup: builder.mutation<unknown, RemoveMemberFromGroupParams>({
			query:
				({ communityId, groupId, userId }) =>
				(client) =>
					client.group({ communityId })({ groupId }).members({ userId }).delete(),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups"],
		}),
		joinGroup: builder.mutation<JoinGroupResponse, GroupMembershipParams>({
			query:
				({ communityId, groupId }): EdenCall<JoinGroupResponse> =>
				(client) =>
					client.group({ communityId })({ groupId }).join.post(),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups", "MyGroupRequests", "Members"],
		}),
		cancelGroupJoinRequest: builder.mutation<unknown, GroupMembershipParams>({
			query:
				({ communityId, groupId }) =>
				(client) =>
					client.group({ communityId })({ groupId }).request.delete(),
			invalidatesTags: ["BrowseGroups", "MyGroupRequests"],
		}),
		decideGroupJoinRequest: builder.mutation<unknown, DecideGroupJoinRequestParams>({
			query:
				({ communityId, requestId, decision }) =>
				(client) =>
					client.group({ communityId }).requests({ requestId }).decide.post({ decision }),
			invalidatesTags: ["GroupJoinRequests", "GroupJoinRequestsCount", "Groups", "Members"],
		}),
		leaveGroup: builder.mutation<unknown, GroupMembershipParams>({
			query:
				({ communityId, groupId }) =>
				(client) =>
					client.group({ communityId })({ groupId }).leave.post(),
			invalidatesTags: ["Groups", "MyGroups", "BrowseGroups", "Members"],
		}),
	}),
});

export const {
	useGetGroupsQuery,
	useGetMyGroupsQuery,
	useGetBrowseGroupsQuery,
	useGetMyGroupRequestsQuery,
	useGetGroupJoinRequestsQuery,
	useGetGroupJoinRequestsCountQuery,
	useCreateGroupMutation,
	useDeleteGroupMutation,
	useUpdateGroupMutation,
	useAddMembersToGroupMutation,
	useRemoveMemberFromGroupMutation,
	useJoinGroupMutation,
	useCancelGroupJoinRequestMutation,
	useDecideGroupJoinRequestMutation,
	useLeaveGroupMutation,
} = groupApi;
